import { create } from "zustand";
import type { ProjectSession } from "@/collab/session";
import type { WorkingFile } from "@/components/ai-chat-ui";
import { deleteFile, readFile, writeFile } from "@/workspace/file-ops";

/**
 * O que cada IA mudou no projeto e a pessoa ainda não revisou: o agente do Kompil e o Claude (pelo
 * motor VS Code). Para cada arquivo, o conteúdo de antes da primeira mudança e o de depois da
 * última. A pessoa revisa (diff), aprova ("Manter") ou desfaz — tudo ou arquivo por arquivo. O
 * Copilot tem o mesmo controle no chat dele. Fica na memória da janela, por projeto.
 */
export type AiSource = "agent" | "claude";

interface Change {
  before: string | null;
  after: string | null;
  at: number;
}

type Sets = Record<string, Record<string, Change>>;

const keyOf = (projectId: string, source: AiSource) => `${projectId.toLowerCase()}:${source}`;

export const useAiChanges = create<{ sets: Sets }>(() => ({ sets: {} }));

/** Antes de a IA mudar `path`: guarda o conteúdo de agora (só da primeira vez). */
export function recordBefore(projectId: string, source: AiSource, path: string, before: string | null) {
  const key = keyOf(projectId, source);
  useAiChanges.setState((s) => {
    const set = s.sets[key] ?? {};
    if (path in set) return s;
    return { sets: { ...s.sets, [key]: { ...set, [path]: { before, after: before, at: Date.now() } } } };
  });
}

/** Depois de a IA mudar `path`: o conteúdo novo (null = apagado). */
export function recordAfter(projectId: string, source: AiSource, path: string, after: string | null) {
  const key = keyOf(projectId, source);
  useAiChanges.setState((s) => {
    const set = s.sets[key] ?? {};
    const prev = set[path];
    if (!prev) return s;
    // Voltou exatamente ao que era: nada a revisar.
    if (prev.before === after) {
      const { [path]: _drop, ...rest } = set;
      return { sets: { ...s.sets, [key]: rest } };
    }
    return { sets: { ...s.sets, [key]: { ...set, [path]: { ...prev, after, at: Date.now() } } } };
  });
}

const EMPTY: WorkingFile[] = [];
const cache = new Map<Record<string, Change>, WorkingFile[]>();

/** Os arquivos alterados por uma IA neste projeto (mais recentes primeiro). */
export function useWorkingFiles(projectId: string, source: AiSource): WorkingFile[] {
  return useAiChanges((s) => {
    const set = s.sets[keyOf(projectId, source)];
    if (!set) return EMPTY;
    let list = cache.get(set);
    if (!list) {
      list = Object.entries(set)
        .sort((a, b) => b[1].at - a[1].at)
        .map(([path, c]) => ({ path, before: c.before, after: c.after }));
      cache.set(set, list);
    }
    return list;
  });
}

const drop = (projectId: string, source: AiSource, paths: string[]) => {
  const key = keyOf(projectId, source);
  useAiChanges.setState((s) => {
    const set = { ...(s.sets[key] ?? {}) };
    for (const p of paths) delete set[p];
    return { sets: { ...s.sets, [key]: set } };
  });
};

/** Aprova (mantém) um arquivo ou todos: sai da revisão, o conteúdo fica como está. */
export function keepChanges(projectId: string, source: AiSource, path?: string) {
  const set = useAiChanges.getState().sets[keyOf(projectId, source)] ?? {};
  drop(projectId, source, path ? [path] : Object.keys(set));
}

/** Desfaz um arquivo ou todos: volta o conteúdo de antes da IA. */
export async function undoChanges(
  session: ProjectSession,
  projectId: string,
  source: AiSource,
  path?: string,
): Promise<number> {
  const set = useAiChanges.getState().sets[keyOf(projectId, source)] ?? {};
  const paths = path ? [path] : Object.keys(set);
  let restored = 0;
  for (const p of paths) {
    const change = set[p];
    if (!change) continue;
    if (change.before === null) {
      if (deleteFile(session, p)) restored++;
    } else {
      await writeFile(session, p, change.before);
      restored++;
    }
  }
  drop(projectId, source, paths);
  return restored;
}

/** Lê o conteúdo atual (para registrar o "depois" de quem não informa o texto novo). */
export const currentContent = (session: ProjectSession, path: string) => readFile(session, path);
